import { store } from "../state/StateStore";
import { ScoreConfig } from "../data/ScoreConfig";
import { getComboMultiplier, getStageFactor } from "../state/Selectors";
import { clamp } from "../utils/Clamp";
import { TowerSystem } from "./TowerSystem";
import { StageConfig } from "../data/StageConfig";

export class ScoreSystem {
  private towerSystem: TowerSystem;

  constructor(towerSystem: TowerSystem) {
    this.towerSystem = towerSystem;
  }

  /** Calculate and apply score for a successful block removal */
  applyScore(swipeSpeed: number): number {
    const state = store.get();

    const comboMultiplier = getComboMultiplier(state);
    const speedFactor = this.calculateSpeedFactor(swipeSpeed);
    const stabilityFactor = this.calculateStabilityFactor();
    const accuracyFactor = this.calculateAccuracyFactor();
    const stageFactor = getStageFactor(state);

    const scoreGain = Math.round(
      ScoreConfig.baseScore *
        comboMultiplier *
        speedFactor *
        accuracyFactor *
        stabilityFactor *
        stageFactor
    );

    // Update state
    store.set({
      score: state.score + scoreGain,
      combo: state.combo + 1,
      lastSwipeSpeed: swipeSpeed,
    });

    return scoreGain;
  }

  /** Reset combo */
  resetCombo(): void {
    store.set({ combo: 0 });
  }

  /** Speed factor: faster = more bonus */
  private calculateSpeedFactor(speed: number): number {
    // Normalize speed: 0-2 px/ms range
    const normalizedSpeed = clamp(speed / 2, 0, 1);
    return 1 + ScoreConfig.speedBonus * normalizedSpeed;
  }

  /** Stability factor: based on tower tilt */
  private calculateStabilityFactor(): number {
    const tilt = this.towerSystem.getTilt();
    // Lower tilt = higher stability factor
    const normalizedStability = 1 - clamp(tilt / StageConfig.tiltThreshold, 0, 1);
    return (
      ScoreConfig.stabilityMin +
      normalizedStability * (ScoreConfig.stabilityMax - ScoreConfig.stabilityMin)
    );
  }

  /** Accuracy factor: based on how centered the tower remains */
  private calculateAccuracyFactor(): number {
    // For now, a simple implementation based on stability
    const tilt = this.towerSystem.getTilt();
    const normalizedAccuracy = 1 - clamp(tilt / (StageConfig.tiltThreshold * 0.5), 0, 1);
    return (
      ScoreConfig.accuracyMin +
      normalizedAccuracy * (ScoreConfig.accuracyMax - ScoreConfig.accuracyMin)
    );
  }
}
